import { useEffect, useRef, useCallback } from 'react';
import { useUserStore } from '@/stores/userStore';
import { useTasksStore } from '@/stores/tasksStore';
import { useScheduleStore } from '@/stores/scheduleStore';
import { taskToUI } from '@flexiplanner/types';

interface WebSocketMessage {
  type: string;
  payload: any;
  senderId?: string;
  timestamp?: number;
}

export function useWebSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const userId = useUserStore(s => s.userId);
  const setConnected = useUserStore(s => s.setConnected);
  const setConnectedUsers = useUserStore(s => s.setConnectedUsers);
  const updateTask = useTasksStore(s => s.updateTask);
  const addTask = useTasksStore(s => s.addTask);
  const deleteTask = useTasksStore(s => s.deleteTask);

  const removeGhostsForTask = useCallback((taskId: string) => {
    const schedule = useScheduleStore.getState();
    schedule.setBlocks(
      schedule.blocks.filter(block => !(block.isGhost && block.taskId === taskId))
    );
    schedule.setGhostBlocks(schedule.ghostBlocks.filter(block => block.taskId !== taskId));
  }, []);

  const connect = useCallback(() => {
    if (!userId) return;

    const token = localStorage.getItem('flexiplanner-session-token') || '';
    const params = new URLSearchParams({ deviceId: crypto.randomUUID() });
    if (token) params.set('token', token);

    let wsUrl = '';
    if ((import.meta as any).env.VITE_API_URL) {
      const url = new URL((import.meta as any).env.VITE_API_URL);
      const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${url.host}/ws/${encodeURIComponent(userId)}?${params.toString()}`;
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/ws/${encodeURIComponent(userId)}?${params.toString()}`;
    }

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setConnected(true);

        // Start heartbeat
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'heartbeat' }));
          }
        }, 30000);
      };

      ws.onmessage = event => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          handleMessage(message);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setConnected(false);
        clearInterval(heartbeatIntervalRef.current);

        // Reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(connect, 3000);
      };

      ws.onerror = error => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [userId, setConnected]);

  const handleMessage = useCallback(
    (message: WebSocketMessage) => {
      switch (message.type) {
        case 'connected':
          setConnectedUsers(message.payload.connectedUsers);
          break;

        case 'user.joined':
        case 'user.left':
          setConnectedUsers(message.payload.connectedUsers);
          break;

        case 'task.updated':
          // Handle both format { taskId, changes } and full task format { id, ... }
          if (message.payload.taskId && message.payload.changes) {
            updateTask(message.payload.taskId, message.payload.changes);
          } else if (message.payload.id) {
            updateTask(message.payload.id, taskToUI(message.payload));
          } else if (message.payload.reordered && message.payload.task_ids) {
            // For now, reload tasks or ignore if UI optimistic update worked
          }
          break;

        case 'task.created':
          addTask(taskToUI(message.payload.task || message.payload));
          break;

        case 'task.deleted':
          // Handle both { taskId } and { id, deleted_ids } formats
          if (message.payload.taskId) {
            deleteTask(message.payload.taskId);
            removeGhostsForTask(message.payload.taskId);
          } else if (message.payload.id) {
            deleteTask(message.payload.id);
            removeGhostsForTask(message.payload.id);
          }
          if (message.payload.deleted_ids && Array.isArray(message.payload.deleted_ids)) {
            message.payload.deleted_ids.forEach((id: string) => {
              deleteTask(id);
              removeGhostsForTask(id);
            });
          }
          break;

        case 'schedule.updated':
        case 'schedule.batch.updated':
          // Refresh ghost blocks
          // You could also emit an event here for the schedule components to handle
          break;

        case 'heartbeat.ack':
          // Heartbeat acknowledged
          break;

        default:
          console.log('Unknown message type:', message.type);
      }
    },
    [updateTask, addTask, deleteTask, removeGhostsForTask, setConnectedUsers]
  );

  const send = useCallback((message: WebSocketMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  const subscribe = useCallback(
    (topics: string[]) => {
      send({ type: 'subscribe', payload: { topics } });
    },
    [send]
  );

  const unsubscribe = useCallback(
    (topics: string[]) => {
      send({ type: 'unsubscribe', payload: { topics } });
    },
    [send]
  );

  const updatePresence = useCallback(
    (view?: string, selectedTaskId?: string) => {
      send({
        type: 'presence',
        payload: { view, selectedTaskId },
      });
    },
    [send]
  );

  const broadcastTaskUpdate = useCallback(
    (taskId: string, changes: any, version: number) => {
      send({
        type: 'task.update',
        payload: { taskId, changes, version },
      });
    },
    [send]
  );

  useEffect(() => {
    connect();

    return () => {
      clearTimeout(reconnectTimeoutRef.current);
      clearInterval(heartbeatIntervalRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  return {
    send,
    subscribe,
    unsubscribe,
    updatePresence,
    broadcastTaskUpdate,
  };
}
